import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  LinearProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { T_KEYS } from '../../constants';

export type ImportingModalStatus = 'loading' | 'success' | 'error';

interface ImportingModalProps {
  open: boolean;
  status?: ImportingModalStatus;
  onGoToDashboard: () => void;
  onStayHere: () => void;
  onRetry?: () => void;
}

const ImportingModal: FC<ImportingModalProps> = ({
  open,
  status = 'loading',
  onGoToDashboard,
  onStayHere,
  onRetry,
}) => {
  const { t } = useTranslation();
  const isRtl = document.documentElement.dir === 'rtl';

  const getIconConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckIcon sx={{ fontSize: 45, color: 'white' }} />,
          gradient: 'linear-gradient(135deg, #06C270 0%, #34D399 100%)',
          shadow: 'rgba(6, 194, 112, 0.25)',
          blur: 'rgba(6, 194, 112, 0.25)',
        };
      case 'error':
        return {
          icon: <CloseIcon sx={{ fontSize: 45, color: 'white' }} />,
          gradient: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)',
          shadow: 'rgba(239, 68, 68, 0.25)',
          blur: 'rgba(239, 68, 68, 0.25)',
        };
      default:
        return {
          icon: <AccessTimeIcon sx={{ fontSize: 45, color: 'white' }} />,
          gradient: 'linear-gradient(135deg, #5B2FF9 0%, #8C73FF 100%)',
          shadow: 'rgba(91, 47, 249, 0.25)',
          blur: 'rgba(91, 47, 249, 0.25)',
        };
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return t(T_KEYS.IMPORTING_MODAL_SUCCESS_TITLE);
      case 'error':
        return t(T_KEYS.IMPORTING_MODAL_FAILED_TITLE);
      default:
        return t(T_KEYS.IMPORTING_MODAL_TITLE);
    }
  };

  const getDescription = () => {
    switch (status) {
      case 'success':
        return t(T_KEYS.IMPORTING_MODAL_SUCCESS_DESC);
      case 'error':
        return t(T_KEYS.IMPORTING_MODAL_FAILED_DESC);
      default:
        return t(T_KEYS.IMPORTING_MODAL_DESC);
    }
  };

  const iconConfig = getIconConfig();

  return (
    <Dialog
      open={open}
      onClose={onStayHere}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          padding: '32px',
          maxWidth: '452px',
          direction: isRtl ? 'rtl' : 'ltr',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(255, 255, 255, 0.48)',
          backdropFilter: 'blur(2.6px)',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '90px',
              height: '90px',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '90px',
                height: '90px',
                borderRadius: '50%',
                background: iconConfig.blur,
                filter: 'blur(16px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: '90px',
                height: '90px',
                borderRadius: '50%',
                background: iconConfig.gradient,
                boxShadow: `0px 4px 12px ${iconConfig.shadow}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {iconConfig.icon}
            </Box>
          </Box>

          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans Arabic", sans-serif',
              fontWeight: 500,
              fontSize: '20px',
              lineHeight: '28px',
              color: '#261B6A',
              textAlign: 'center',
            }}
          >
            {getTitle()}
          </Typography>

          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans Arabic", sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#6B6B6B',
              textAlign: 'center',
            }}
          >
            {getDescription()}
          </Typography>

          {status === 'loading' && (
            <Box sx={{ width: '313px' }}>
              <LinearProgress
                sx={{
                  height: '5px',
                  borderRadius: '9999px',
                  backgroundColor: '#EAE3FE',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #5B2FF9 0%, #8C73FF 100%)',
                    borderRadius: '9999px',
                  },
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%',
            }}
          >
            <Button
              variant="contained"
              onClick={onGoToDashboard}
              sx={{
                backgroundColor: '#5B3FFF',
                color: 'white',
                borderRadius: '8px',
                padding: '16px 97px',
                height: '56px',
                fontFamily: '"Tajawal", sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '-0.3125px',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#4A32CC',
                  boxShadow: 'none',
                },
              }}
            >
              {t(T_KEYS.GO_TO_DASHBOARD)}
            </Button>
            {status === 'error' && onRetry ? (
              <Button
                variant="contained"
                onClick={onRetry}
                sx={{
                  backgroundColor: '#F2F2F2',
                  color: '#262626',
                  borderRadius: '8px',
                  padding: '16px 97px',
                  height: '44px',
                  fontFamily: '"Tajawal", sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '-0.3125px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#E5E5E5',
                    boxShadow: 'none',
                  },
                }}
              >
                {t(T_KEYS.IMPORTING_MODAL_TRY_AGAIN)}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={onStayHere}
                sx={{
                  backgroundColor: '#F2F2F2',
                  color: '#262626',
                  borderRadius: '8px',
                  padding: '16px 97px',
                  height: '44px',
                  fontFamily: '"Tajawal", sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '-0.3125px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#E5E5E5',
                    boxShadow: 'none',
                  },
                }}
              >
                {t(T_KEYS.STAY_HERE)}
              </Button>
            )}
          </Box>

          {status === 'loading' && (
            <Typography
              sx={{
                fontFamily: '"IBM Plex Sans Arabic", sans-serif',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#9A9A9A',
                textAlign: 'center',
              }}
            >
              {t(T_KEYS.IMPORTING_MODAL_NOTIFICATION)}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

ImportingModal.displayName = 'ImportingModal';

export default ImportingModal;
